import Vue from 'vue'
import debug from 'debug'
import Project from '@/models/project'
import {
  intersection,
  map,
  merge
} from 'lodash'

const trace = debug('vuex:projects')

export default {
  namespaced: true,

  state: {
    // 首頁專案清單預設每頁顯示的資料筆數
    itemsPerPage: 30,

    // id 清單儲存欄位
    list: [],

    // 物件實例快取
    cached: {},

    // 首頁清單讀取狀態
    loading: false,

    // 首頁清單篩選的專案 id 陣列（`[]`），若沒有過濾清單則為 `null`
    filterList: null
  },

  actions: {
    // 依照篩選條件（通常指專案狀態）、排序依賴的欄位名稱、排序規則，取得新的 id 清單
    getList({ commit, dispatch }, { type, sort_type, sort_by, name, serial } = {}) {
      trace('getting list')
      return Project.list({ type, sort_type, sort_by, name, serial })
        .then(ids => {
          commit('SET_LIST', { ids })
          dispatch('activeItems')
        })
        .catch(err => {
          commit('SET_LIST', { ids: [] })
          throw err
        })
    },

    // 淘汰過期的物件，將需要重新向遠端獲取新的物件的專案 id 整理出來，
    // 或是篩選出尚未取得過物件實例的 id， 重新向伺服器再要一次整筆物件清單。
    activeItems({ commit, state, dispatch, getters }) {
      const ids = getters.activeIds.filter(id => {
        const item = state.cached[id]
        if (item) {
          return item.isExpired
        } else {
          return true
        }
      })
      trace('active items: ', ids)
      return dispatch('fetchItems', { ids })
    },

    // 向伺服器取得給予的 ids 物件實例
    fetchItems({ commit, getters }, { ids }) {
      trace('fetching items: ', ids)

      if (!ids.length) {
        return Promise.resolve([])
      }

      trace('sending request to server...')
      commit('LOADING')
      return Project.where(ids)
        .then(projects => {
          projects.forEach(project => commit('ADD', { project }))
          return projects
        })
        .finally(() => {
          commit('LOADED')
        })
    },

    // 向伺服器取得指定 id 的物件實例
    fetchItem({ commit, dispatch }, { id }) {
      trace('fetching item:', id)
      return Project.get(id)
        .then(project => {
          commit('ADD', { project })
          if (project.blocks) {
            dispatch('editor/blocks/add', { blocks: project.blocks }, { root: true })
          }
          return project
        })
    },

    // 向伺服器新增專案資料
    create({ commit }, { name, galleries, serial, sheet_url, sheet_name }) {
      return Project.create({ name, galleries, serial, sheet_url, sheet_name })
        .then(project => {
          commit('ADD', { project })
          return project
        })
    },

    // 更新本機端物件資料，不會向伺服器發送更新請求
    assign({ commit }, { id, data }) {
      commit('UPDATE', { id, data })
    },

    // 更新指定 id 的伺服器端資料，同時更新本機端物件資料
    update({ rootGetters, commit, dispatch }, { id, data }) {
      return Project.update(id, data)
        .then(res => {
          // 向 API 儲存送的是「純 id」例如 [1, 3, 5]，但是資料使用上需要的是「實例物件」陣列，
          // 因此需要做反轉還原，把 id 轉回物件，再提交 UPDATE。
          //
          // 此外，由於更新專案並不一定是整個完整專案物件都往 API 丟，所以需要判斷 `data.galleries`
          // 是否存在，有資料才要做轉換處理
          //
          // TODO: 這個處理其實設計得不好，需要找個時間重構，理論上這邊應該要保持單純，不要有太多額外的操作
          if (data.galleries) {
            const galleries = rootGetters['galleries/items']
            data = merge(data, {
              galleries: map(data.galleries, id => galleries[id])
            })
          }

          commit('UPDATE', { id, data, updatedAt: res.updated_at })
        })
        .catch(err => {
          if (err) {
            dispatch('alert/set', { code: err.code }, { root: true })
            throw err
          }
        })
    },

    // 同時刪除多筆 ids 在伺服器端 / 本機端的資料
    delete({ commit }, ids) {
      return Project.delete(ids)
        .then(() => commit('DELETE', ids))
    },

    // 設定過濾清單，list 應為多筆專案 id 的集合
    setFilterList({ commit }, list) {
      return Promise.resolve()
        .then(() => commit('SET_FILTER_LIST', list))
    },

    // 向伺服器獲取指定 id 及類型的專案預覽頁面
    getPreview({ commit, dispatch }, { id, type }) {
      return Project.preview(id, type)
        .catch(err => {
          if (err) {
            dispatch('alert/set', { code: err.code }, { root: true })
            throw err
          }
        })
    },

    // 向伺服器發送對指定 id 的專案做同步商品資料的請求
    sync({ dispatch, commit }, id) {
      return Project.sync(id)
        .then(project => {
          if (project) {
            commit('UPDATE', { id, data: null, updatedAt: project.updated_at })
          }
          return project
        })
        .catch(err => {
          if (err) {
            dispatch('alert/set', { code: err.code }, { root: true })
            throw err
          }
        })
    },

    // 向伺服器獲取指定 id 專案的 EDM 下載資料
    download({ commit }, id) {
      return Project.download(id)
    },

    // 向伺服器發送對指定 id 專案做發佈的請求
    publish({ commit }, id) {
      return Project.publish(id)
        .then(data => {
          commit('UPDATE', { id, data })
          return data
        })
    },

    copy({ commit }, { id, name, serial }) {
      return Project.copy({ id, name, serial })
        .then(project => {
          commit('ADD', { project })
          return project
        })
    }
  },

  mutations: {
    SET_LIST(state, { ids }) {
      state.list = ids
    },

    ADD(state, { project }) {
      // init project
      project = new Project(project)

      // append to list
      if (!state.list.includes(project.id)) {
        state.list.unshift(project.id)
      }

      // set item
      Vue.set(state.cached, project.id, project)
    },

    UPDATE(state, { id, data, updatedAt }) {
      const project = state.cached[id]
      if (updatedAt) {
        project.updated_at = updatedAt
      }
      if (data) {
        Object.assign(project, data)
      }
    },

    DELETE(state, ids) {
      ids.forEach(id => {
        const index = state.list.indexOf(id)
        if (index !== -1) {
          Vue.delete(state.list, index)
        }
        Vue.delete(state.cached, id)
      })
    },

    LOADING(state) {
      state.loading = true
    },

    LOADED(state) {
      state.loading = false
    },

    SET_FILTER_LIST(state, list) {
      state.filterList = list
    }
  },

  getters: {
    filteredIds(state) {
      const { list, filterList } = state
      return filterList ? intersection(list, filterList) : []
    },

    activeIds(state, getters, rootState) {
      const { itemsPerPage, list, filterList } = state
      const { filteredIds } = getters

      const page = Number(rootState.route.query.page) || 1
      const start = (page - 1) * itemsPerPage
      const end = page * itemsPerPage
      const items = filterList ? filteredIds : list

      return items.slice(start, end)
    },

    // these items in the array may not be fully fetched.
    items(state, getters) {
      return map(getters.activeIds, id => state.cached[id]).filter(_ => _)
    },

    item(state) {
      return function (id) {
        return state.cached[id]
      }
    }
  }
}



// WEBPACK FOOTER //
// ./src/store/modules/projects.js